import { useState, useEffect } from "react";
import FilterComponent from "../../components/FilterComponent";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import reload from "../../assets/reload.svg";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import React from "react";
import {
  useGetInventoryProjectionSOQMutation,
  useGetForecastOverviewDropDownFilterMutation,
  useGetInventoryProjectionSOQLineChartMutation,
} from "../../services/redux/api/inventorySoq";
import { Button, Table } from "rsuite";
import Graph from "../../common/Graph";
import { formatNumber } from "../../utils/formatter";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { IRoot } from "../../services/interface/IHome";
import { IRoot as IDemandRoot } from "../../services/interface/IDemand";
import { setApplyFilter } from "../../services/redux/reducer/demandfilter.reducer";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";

interface InventoryValue {
  weekStartDate: string;
  attributeValue: number | string;
}

interface InventoryRow {
  attribute: string;
  values: InventoryValue[];
}

const InventorySoq = () => {
  const initialState:{[key:string]:string[]} = {
    dc: [],
    category: [],
    subCategory: [],
    productSegm: [],
    productDesc: [],
    itemId: ["336801"],
  }
  const dispatch = useDispatch();
  const [graphData, setGraphData] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [selectedFilterData, setSelectedFilterData] = useState<{
    [key: string]: string[];
  }>(initialState);
  
  const [filters, setFilters] = useState({
    dc: [],
    category: [],
    subCategory: [],
    productSegm: [],
    productDesc: [],
    itemId: [],
    vendorName: [],
  });
  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any>(null);
  const [inventoryData, setInventoryData] = useState<{
    [key: string]: { weekStartDate: string; attributeValue: any }[];
  }>({});

  const [
    getInventoryProjectionSOQ,
    {
      isLoading: isProjectionLoading,
      error: projectionError,
      data: projectionData,
    },
  ] = useGetInventoryProjectionSOQMutation();
  const [getForecastOverviewDropDownFilter, { isLoading, error }] =
    useGetForecastOverviewDropDownFilterMutation();
  const [getLineChartData] = useGetInventoryProjectionSOQLineChartMutation();
  const windowSize = useSelector((state:IRoot)=>state.filter.windowSize);
  const applyFilter = useSelector((state:IDemandRoot)=>state.dbfilter.applyFilter);
  const lastUpdated = useSelector((state:IRoot)=>state.filter.lastUpdateDate) || "";

  const filterNamesMap:{ [key: string]: string } = {
    dc: "DC",
    Dc: "DC",
    productSegm: "Segmentation",
    vendorName: "Vendor",
  };

  const filterShowHandler = () => {
    setIsVisible((prev) => !prev);
  };

  const graphHandler=async(payload: any)=>{
    // API Call
    const result = await getLineChartData(payload);
    const data = result?.data?.data?.futureDOS || [];
    let finalData:any = [];
    Array.isArray(data) && data?.map((each) => {
      let obj = {
        ...each,
        "historicalDos": each?.historicalDos ?? 0,
        "forecastDate": moment(each?.dayDate?.slice(0, 10)).format("DD-MMM")
      }
      finalData.push(obj);
    });
    setGraphData(finalData);
  }

  const applyHandler=()=>{
    dispatch(setApplyFilter("applied"));
  }

  const removeHandler=()=>{
    setSelectedFilterData(initialState);
    dispatch(setApplyFilter(""));
  }

  const handleFilterChange = (filterKey: string, value: string | string[]) => {
    const newValue = Array.isArray(value) ? value : [value];
    
    setSelectedFilterData(prevState => ({
      ...prevState,
      [filterKey]: newValue,
    }));
  };

  const callForecastFilterApi = async () => {
    try {
      const response = await getForecastOverviewDropDownFilter(
        selectedFilterData as any
      ).unwrap();
      setFilters(prevFilters => ({
        dc: response?.data?.dc || [],
        category: response?.data?.category || [],
        subCategory: response?.data?.subCategory || [],
        productSegm: response?.data?.productSegm || [],
        productDesc: response?.data?.productDesc || [],
        itemId: response?.data?.itemId || [],
        vendorName: response?.data?.vendorName || [],
      }));

    } catch (error) {
      console.error("Failed to fetch forecast filter data:", error);
    }
  };

  const transformData = (apiResponse: any) => { 
    // Define the desired order of attributes
    const attributeOrder = [
        "Week's Opening Inventory",
        "Actual Demand",
        "Forecasted Demand",
        "SOQ",
        "PO Received",
        "Open PO"
    ];
    
    // Create rows from the data in the specified order
    const rows = attributeOrder.map((attribute) => {
        const items = apiResponse[attribute] || []; // Get items for the attribute or an empty array
        return {
            attribute: attribute,
            values: items.map((item: any) => ({
                weekStartDate: item.data.weekStartDate,
                attributeValue: item.data.attributeValue,
                weeksummary: item.weeksummary,
            })),
        };
    });

    return rows; 
};
  
  const callInventorySOQTableApi = async () => {
    try {
      const response = await getInventoryProjectionSOQ(
        selectedFilterData as any
      ).unwrap();
      
      setTableData(response.data);
      const transformedRows = transformData(response.data);
      setInventoryData(response.data);

    } catch (error) {
      console.error("Failed to fetch inventory projection data:", error);
    }
  };

  const getUniqueColumns = (data: any) => {
    // Get first key's data to extract dates (assuming all keys have same dates)
    const firstKey = Object.keys(data)[0];
    if (!firstKey || !data[firstKey]) return [];
    
    return data[firstKey].map((item: any) => ({
      weekStartDate: item.data.weekStartDate,
      weeksummary: item.weeksummary,
    }));
  };

  const rows = React.useMemo(() => {
    if (!tableData) return [];
    return transformData(tableData);
  }, [tableData]);

  const columns = React.useMemo(() => {
    if (!tableData) return [];
    return getUniqueColumns(tableData);
  }, [tableData]);

  const formatFilterName = (filterKey: string) =>
    filterKey
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
   
  useEffect(() => {
    callForecastFilterApi();
  }, []);

  useEffect(()=>{
    if(applyFilter === "" || applyFilter === "applied"){
      graphHandler(selectedFilterData);  
    }
  },[applyFilter]);

  useEffect(() => {
    if(applyFilter == "" || applyFilter == "applied"){
      callInventorySOQTableApi();
    }
  }, [applyFilter]);

  // Table Header - Start
const headerGroup = (
  <ColumnGroup>
    <Row>
      <Column 
        className="text-center" 
        align="center" 
        header={<div className="table-header-title font-semibold">Attribute</div>} 
        rowSpan={3} 
      />
      <Column 
        className="text-center" 
        align="center" 
        header={<div className="table-header-title font-semibold">A. Historical Actuals</div>} 
        colSpan={4} 
      />
      <Column 
        className="text-center" 
        align="center" 
        header={<div className="table-header-title font-semibold">B. Ongoing</div>} 
        colSpan={1} 
      />
      <Column 
        className="text-center" 
        align="center" 
        header={<div className="table-header-title font-semibold">C. Future</div>} 
        colSpan={2} 
      />
    </Row>
    <Row>
      {columns.slice(0, 4).map((col: { weekStartDate: string }, index: number) => (
        <Column 
          className="text-left" 
          header={
            <div className="table-header-title font-semibold">
              {moment(col?.weekStartDate?.slice(0, 10)).format("MM/DD/YYYY")}
            </div>
          } 
          key={col?.weekStartDate}
        />
      ))}
      {columns.slice(4, 5).map((col: { weekStartDate: string }) => (
        <Column 
          className="text-left" 
          header={
            <div className="table-header-title font-semibold">
              {moment(col?.weekStartDate?.slice(0, 10)).format("MM/DD/YYYY")}
            </div>
          } 
          key={col?.weekStartDate}
        />
      ))}
      {columns.slice(5).map((col: { weekStartDate: string }, index: number) => (
        <Column 
          className="text-left" 
          header={
            <div className="table-header-title font-semibold">
              {moment(col?.weekStartDate?.slice(0, 10)).format("MM/DD/YYYY")}
            </div>
          } 
          key={col?.weekStartDate}
        />
      ))}
    </Row>
  </ColumnGroup>
);

  const dateTemplate = (rowData: any, field: any) => {
    const dateValue = rowData.values.find(
      (v: { weekStartDate: any; }) => v?.weekStartDate === field?.field
    );
    return (!dateValue?.attributeValue ? 0 : formatNumber(dateValue?.attributeValue));
  };
  // Table Header - End

  return (
    <>
      <div className="relative">
        <div className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${!isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"}`}>
          <div className="flex justify-between items-center mb-1">
            <h3 className="page-title font-semibold">{"Inventory & Replenishment > Inventory Projection & SOQ"}</h3>
            {<div className="flex gap-2">
                    <img src={reload} alt="reload" className="w-[1vw]" />
                    <p className="text-fs16 text-[#858585] font-semibold">Last Updated : <span>{moment(lastUpdated?.slice(0, 10)).format("M/DD/YYYY")}</span></p>
            </div>}
          </div>

          {/* Filters Section */}
          {isVisible && <div className="grid grid-cols-[1fr_auto] items-end gap-4 filter-div">
            <div className="grid grid-cols-10 gap-2 items-end">
              {filters &&
                Object.entries(filters).map(
                  ([filterKey, filterArray]: [string, any[]]) => (
                    <FilterComponent
                      loading={isLoading}
                      key={filterKey}
                      filterKey={filterKey}
                      filterName={filterNamesMap[filterKey] || formatFilterName(filterKey)}
                      options={filterArray.map((opt) => ({
                        label: opt.filterVal,
                        value: opt.filterValue || opt.filterVal,
                      }))}
                      labelKey="label"
                      valueKey="value"
                      value={selectedFilterData[filterKey] || []}
                      onFilterChange={handleFilterChange}
                      onDropdownOpen={callForecastFilterApi}
                    />
                  )
                )}
            </div>
            <div className="align-right flex gap-1">
              <Button onClick={removeHandler} className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text">
                Clear
              </Button>
              <Button onClick={applyHandler} className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text">
                Apply
              </Button>
            </div>
          </div>}

          <div className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer z-[10]" onClick={filterShowHandler}>
                {isVisible && <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />}
                {!isVisible && <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />}
          </div>
        </div>
      </div>

<div className={`bg-[#F1F1F1] px-[2%] py-2 pb-2 ${isVisible ? "" : "h-[40vh]"}`}>
    <DataTable 
        value={rows}
        emptyMessage="No Records Found"
        className="w-[88%] h-full p-table-container bg-[#fff]" 
        headerColumnGroup={headerGroup}
    >
        <Column 
            field="attribute" 
            body={(rowData) => (
                <div className="table-cell-title font-normal">
                    {rowData.attribute}
                </div>
            )}
        />

        {columns?.slice(0, 4).map((col: any, index: any) => (
            <Column 
                key={index} 
                field={col?.weekStartDate} 
                body={(rowData, column) => (
                    <div className="table-cell-title font-normal">
                        {dateTemplate(rowData, column)}
                    </div>
                )}
                className="text-left"
            />
        ))}

        {columns?.slice(4, 5).map((col: any, index: any) => (
            <Column 
                key={index} 
                field={col?.weekStartDate} 
                body={(rowData, column) => (
                    <div className="table-cell-title font-normal">
                        {dateTemplate(rowData, column)}
                    </div>
                )}
                className="text-left"
            />
        ))}

        {columns?.slice(5, 8).map((col: any, index: any) => (
            <Column 
                key={index} 
                field={col?.weekStartDate} 
                body={(rowData, column) => (
                    <div className="table-cell-title font-normal">
                        {dateTemplate(rowData, column)}
                    </div>
                )}
                className="text-left"
            />
        ))}
    </DataTable>
</div>

      <div className={`bg-[#F1F1F1] flex px-[2%] pb-2 h-[30vh]`}>
        <Graph
          title="Days of Supply (Last 4 Weeks + Next 3 Weeks)"
          graphTypes={["line","area","line","area"]}
          data={graphData}
          dataKey={["historicalDos","minTargetdays","futureDOS","maxTargetDays"]}
          dataName={["Historical DOS","Min Target Days","Future DOS","Max Target Days"]}
          fill={["#012169","#012169bf","#86BC25","#62B5E5"]}
          onClick={()=>{}}
          margin={{ top: 20, right: 20, bottom: 0, left: -20 }}
          xAxisKey="forecastDate"
        />
      </div>
      {isProjectionLoading && <CustomLoader open={isProjectionLoading} />}
    </>
  );
};

export default InventorySoq;